import React, { useMemo } from "react";
import CreatableSelect from "react-select/creatable";
import { useTranslation } from "react-i18next";

export const toCreatableOptions = (list = []) =>
	list.map((item) => ({
		label: item.name,
		value: item.name,
	}));

export const toCreatableValue = (text) => {
	const value = String(text || "").trim();
	return value ? { label: value, value } : null;
};

export const fromCreatableValue = (option) => option?.value ?? "";

const getSelectTheme = (isDark) => (theme) => ({
	...theme,
	colors: {
		...theme.colors,
		primary: isDark ? "#6b7fd7" : "#24695c",
		primary25: isDark ? "#373737" : "#e6f7ff",
		primary50: isDark ? "#4a5464" : "#d6e8ff",
		neutral0: isDark ? "#262932" : "#ffffff",
		neutral20: isDark ? "#4a5464" : "#d0d5dd",
		neutral30: isDark ? "#5c6678" : "#cbd5e1",
		neutral40: isDark ? "#8b95a8" : "#98a2b3",
		neutral50: isDark ? "#a8b0be" : "#98a2b3",
		neutral80: isDark ? "#e8eaef" : "#344054",
	},
});

const getSelectStyles = (isDark, isCompact, useMenuPortal) => ({
	control: (base, state) => ({
		...base,
		minHeight: isCompact ? 32 : 36,
		fontSize: isCompact ? "0.8125rem" : "0.875rem",
		borderRadius: 8,
		borderColor: state.isFocused
			? isDark
				? "#6b7fd7"
				: "#24695c"
			: isDark
				? "#4a5464"
				: "#d0d5dd",
		boxShadow: state.isFocused
			? isDark
				? "0 0 0 3px rgba(107, 127, 215, 0.2)"
				: "0 0 0 3px rgba(36, 105, 92, 0.12)"
			: "none",
		"&:hover": {
			borderColor: isDark ? "#6b7fd7" : "#24695c",
		},
	}),
	valueContainer: (base) => ({
		...base,
		padding: isCompact ? "0 6px" : "0 8px",
	}),
	input: (base) => ({
		...base,
		margin: 0,
		padding: 0,
	}),
	indicatorsContainer: (base) => ({
		...base,
		height: isCompact ? 30 : 34,
	}),
	dropdownIndicator: (base) => ({
		...base,
		padding: isCompact ? "0 6px" : "0 8px",
	}),
	clearIndicator: (base) => ({
		...base,
		padding: isCompact ? "0 4px" : "0 6px",
	}),
	menu: (base) => ({
		...base,
		fontSize: isCompact ? "0.8125rem" : "0.875rem",
		zIndex: useMenuPortal ? undefined : 20,
	}),
	menuPortal: (base) => ({
		...base,
		zIndex: 2000,
	}),
	option: (base) => ({
		...base,
		fontSize: isCompact ? "0.8125rem" : "0.875rem",
		padding: isCompact ? "6px 10px" : "8px 12px",
	}),
	placeholder: (base) => ({
		...base,
		color: isDark ? "#8b95a8" : "#98a2b3",
	}),
	singleValue: (base) => ({
		...base,
		color: isDark ? "#e8eaef" : "#344054",
	}),
});

const CreatableOptionSelect = ({
	options = [],
	value = "",
	onChange,
	placeholder,
	isDisabled = false,
	isCompact = false,
	inModal = false,
	className = "",
	createLabel,
}) => {
	const { t } = useTranslation();
	const isDark = document.body.classList.contains("dark-only");
	const selectOptions = useMemo(() => toCreatableOptions(options), [options]);
	const selectedValue = useMemo(() => toCreatableValue(value), [value]);
	// Portaling breaks alignment inside Bootstrap/Reactstrap modals (transform + fixed).
	const useMenuPortal = !inModal;

	return (
		<CreatableSelect
			className={className}
			classNamePrefix="creatable-option"
			options={selectOptions}
			value={selectedValue}
			onChange={(option) => onChange?.(fromCreatableValue(option))}
			isClearable
			isSearchable
			isDisabled={isDisabled}
			placeholder={placeholder || t("Search or type...")}
			formatCreateLabel={(inputValue) =>
				createLabel ? createLabel(inputValue) : `${t("Use")} "${inputValue}"`
			}
			noOptionsMessage={() => t("Type to add a custom value")}
			theme={getSelectTheme(isDark)}
			styles={getSelectStyles(isDark, isCompact, useMenuPortal)}
			menuPortalTarget={useMenuPortal && typeof document !== "undefined" ? document.body : null}
			menuPosition={useMenuPortal ? "fixed" : "absolute"}
			menuPlacement="auto"
			menuShouldScrollIntoView={false}
		/>
	);
};

export default CreatableOptionSelect;
